/**
 * 📨 First Interviews — self-contained tab section: renders the tab, owns the
 * bulk-invite modal and hands the confirmed batch to the board's countdown.
 *
 * Same shape as FinalRoundsSection, and for the same reason: TAActionBoardView is
 * against the repo's 500-line limit, and the tab's own state (which candidates
 * are being reviewed) is of no interest to the rest of the board.
 */
import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { Mail } from 'lucide-react';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import type { FirstInvitePreviewCandidate } from '../../services/firstInviteApi';
import type { RoleBoard } from '../../types/taActionBoard';
import { bulkInviteAction } from './bulkInviteAction';
import { BulkInviteModal, type BulkInviteState } from './BulkInviteModal';
import { FirstInterviewsTab } from './FirstInterviewsTab';
import { useGmailConnection } from './GmailConnectionContext';
import type { PendingAction } from './usePendingActions';
import type { FocusTarget } from './useFocusCard';

export function FirstInterviewsSection({
  roles,
  handled,
  focus = null,
  boardEmail,
  onChanged,
  senderName,
  onMarkHandled,
  onUnmarkHandled,
  onDefer,
  onGoToSla,
}: {
  roles: RoleBoard[];
  handled: Record<string, string>;
  /** Candidate the Overdue tab sent us to — passed straight through to the tab. */
  focus?: FocusTarget | null;
  boardEmail: string;
  /** Reload the board — the shared pipeline strip's Lever retry. */
  onChanged?: () => void;
  /** The signed-in user's name — the invite is signed and sent as them. */
  senderName: string;
  onMarkHandled: (opportunityId: string, label: string) => void;
  onUnmarkHandled: (opportunityId: string) => void;
  onDefer: (action: Omit<PendingAction, 'secondsLeft'>) => void;
  /** Open the ⏰ SLA tab — where an already-invited candidate is chased. */
  onGoToSla?: () => void;
}) {
  const [modal, setModal] = useState<BulkInviteState | null>(null);
  const gmail = useGmailConnection();

  return (
    <>
      {!gmail.connected && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
          <p className="text-sm text-gray-600">
            Invitations are sent from <strong>your own</strong> Gmail — connect it to send this batch.
            You can review the drafts either way.
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void gmail.connect()}
            loading={gmail.connecting}
            className="rounded-full px-4"
          >
            <Mail className="mr-1.5 h-3.5 w-3.5" /> Connect Gmail
          </Button>
        </div>
      )}

      <FirstInterviewsTab
        roles={roles}
        boardEmail={boardEmail}
        onChanged={onChanged}
        handled={handled}
        focus={focus}
        onGoToSla={onGoToSla}
        onBulkInvite={(role, candidates) => {
          setModal({ role, candidates });
          void logAnalyticsEvent('ta_first_invite_review_opened', {
            candidates: candidates.length,
          });
        }}
      />

      <BulkInviteModal
        state={modal}
        boardEmail={boardEmail}
        senderName={senderName}
        onClose={() => setModal(null)}
        onConfirm={(role, batch, language) => {
          setModal(null);
          if (!gmail.connected) {
            // Refuse rather than defer: a countdown that ends in "not connected"
            // looks exactly like a send that happened.
            showToast({
              status: 'error',
              description: 'Connect your Google account first — nothing was sent.',
            });
            return;
          }
          confirmBatch({ role, batch, language, onMarkHandled, onUnmarkHandled, onDefer, gmailToken: gmail.token });
        }}
      />
    </>
  );
}

/**
 * Mark the batch as handled immediately, then queue the real send behind the
 * countdown. Marking first is deliberate: the cards must stop looking actionable
 * the moment the recruiter confirms, and `undo` puts every one of them back.
 */
function confirmBatch({
  role,
  batch,
  language,
  gmailToken,
  onMarkHandled,
  onUnmarkHandled,
  onDefer,
}: {
  role: RoleBoard;
  batch: FirstInvitePreviewCandidate[];
  language: 'de' | 'en';
  gmailToken: string | null;
  onMarkHandled: (opportunityId: string, label: string) => void;
  onUnmarkHandled: (opportunityId: string) => void;
  onDefer: (action: Omit<PendingAction, 'secondsLeft'>) => void;
}) {
  for (const candidate of batch) onMarkHandled(candidate.opportunityId, 'Invite sent');
  onDefer(
    bulkInviteAction({
      role,
      batch,
      gmailToken,
      unmarkHandled: onUnmarkHandled,
    })
  );
  // Counts and language only — never candidate names or addresses (PII rule).
  void logAnalyticsEvent('ta_first_invite_confirmed', {
    candidates: batch.length,
    language,
    flagged: batch.filter((c) => (c.warnings || []).length > 0).length,
  });
}
